import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { BrandLogo } from "@/components/BrandCard";
import { SampleTag } from "@/components/AuctionCard";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/brand";
import { PhoneCall, MessageCircle, Wallet, Ruler, TrendingUp, Timer, Store, CalendarDays, MapPin, Check } from "lucide-react";

export default function BrandDetail() {
    const { slug } = useParams();
    const [b, setB] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        api.get(`/brands/${slug}`).then((r) => setB(r.data)).catch(() => setMissing(true));
    }, [slug]);

    if (missing) {
        return (
            <PageShell navVariant="dark">
                <div className="pt-40 pb-24 text-center" data-testid="brand-not-found">
                    <h1 className="font-serif text-4xl">Brand not found</h1>
                    <Link to="/brands" className="mt-6 inline-block text-[var(--ebg-gold)] font-bold">Back to All Brands</Link>
                </div>
            </PageShell>
        );
    }
    if (!b) return <PageShell navVariant="dark"><div className="pt-40 pb-24 text-center text-white/40 bg-[#0a0d18] min-h-screen">Loading…</div></PageShell>;

    const msg = `Hi EBG Realty, I'm interested in the ${b.name} (${b.opportunity_type}) opportunity. Please share details.`;
    const facts = [
        { Icon: Wallet,       l: "Investment",       v: b.investment_range, k: "investment" },
        { Icon: Ruler,        l: "Space Required",   v: b.area_required, k: "area" },
        { Icon: TrendingUp,   l: "Expected Returns", v: b.expected_roi, k: "roi" },
        { Icon: Timer,        l: "Payback / Tenure", v: b.payback_period, k: "payback" },
        { Icon: Store,        l: "Outlets",          v: b.outlets, k: "outlets" },
        { Icon: CalendarDays, l: "Established",      v: b.established, k: "established" },
    ].filter((f) => f.v);
    const gallery = [b.cover_image, ...(b.images || [])].filter(Boolean);

    return (
        <PageShell navVariant="dark">
            <div data-testid="brand-detail">
                <section className="relative bg-[#0a0d18] text-white pt-28 pb-14 overflow-hidden">
                    {b.cover_image && <img src={b.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/>}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/85 to-[#0a0d18]/40"/>
                    <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
                        <nav className="text-[11px] text-white/50 mb-8 flex items-center gap-2"><Link to="/" className="hover:text-[var(--ebg-gold)]">Home</Link><span>›</span><Link to="/brands" className="hover:text-[var(--ebg-gold)]">All Brands</Link><span>›</span><span className="text-white">{b.name}</span></nav>
                        <div className="flex items-start gap-5">
                            <BrandLogo brand={b} size="w-20 h-20 md:w-24 md:h-24" text="text-2xl md:text-3xl"/>
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="bg-[var(--ebg-gold)] text-black px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="brand-opportunity">{b.opportunity_type}</span>
                                    <span className="border border-white/25 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">{b.category}</span>
                                    <SampleTag show={b.is_sample}/>
                                </div>
                                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3" data-testid="brand-name">{b.name}</h1>
                                {b.tagline && <p className="text-white/70 mt-3 max-w-2xl">{b.tagline}</p>}
                            </div>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href={CONTACT.tel} data-testid="brand-call" className="bg-[var(--ebg-gold)] text-black px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-white transition-colors inline-flex items-center gap-2"><PhoneCall size={13}/> Call EBG · {CONTACT.display}</a>
                            <a href={waLink(msg)} target="_blank" rel="noopener noreferrer" data-testid="brand-whatsapp" className="border border-white/40 px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-600 hover:border-emerald-600 transition-colors inline-flex items-center gap-2"><MessageCircle size={13}/> WhatsApp EBG</a>
                        </div>
                    </div>
                </section>

                <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12 pb-28 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 space-y-12">
                        {facts.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10" data-testid="brand-facts">
                                {facts.map(({ Icon, l, v, k }) => (
                                    <div key={k} className="bg-white p-5" data-testid={`brand-fact-${k}`}>
                                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-black/45"><Icon size={12} className="text-[var(--ebg-gold)]"/>{l}</div>
                                        <div className="font-bold mt-2 text-[15px]">{v}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div>
                            <h2 className="font-serif text-3xl md:text-4xl">About the opportunity</h2>
                            <p className="text-black/70 leading-relaxed mt-4 whitespace-pre-line" data-testid="brand-description">{b.description}</p>
                            {b.highlights?.length > 0 && (
                                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {b.highlights.map((h, i) => <li key={i} className="flex items-start gap-3 text-[14px] text-black/75"><Check size={14} className="text-[var(--ebg-gold)] mt-1 shrink-0"/>{h}</li>)}
                                </ul>
                            )}
                        </div>
                        {gallery.length > 1 && (
                            <div className="grid sm:grid-cols-2 gap-4" data-testid="brand-gallery">
                                {gallery.slice(1, 5).map((src, i) => <img key={i} src={src} alt="" className="w-full aspect-[4/3] object-cover border border-black/8"/>)}
                            </div>
                        )}
                    </div>
                    <aside className="lg:col-span-4">
                        <div className="sticky top-28 space-y-4">
                            {b.preferred_locations?.length > 0 && (
                                <div className="bg-white border border-black/8 p-6" data-testid="brand-locations">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/45">Preferred Locations</div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {b.preferred_locations.map((l) => <span key={l} className="inline-flex items-center gap-1 border border-black/12 px-2.5 py-1 text-[12px]"><MapPin size={11} className="text-[var(--ebg-gold)]"/>{l}</span>)}
                                    </div>
                                </div>
                            )}
                            <div className="bg-[#0a0d18] text-white p-6" data-testid="brand-contact-card">
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">EBG Commercial Desk</div>
                                <div className="font-serif text-2xl mt-2">Interested in {b.name}?</div>
                                <p className="text-white/60 text-[13px] mt-2">Our team will connect you with the brand and help shortlist the right space.</p>
                                <a href={CONTACT.tel} data-testid="brand-sidebar-call" className="mt-5 w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold inline-flex items-center justify-center gap-2 hover:bg-white"><PhoneCall size={13}/> {CONTACT.display}</a>
                                <a href={waLink(msg)} target="_blank" rel="noopener noreferrer" data-testid="brand-sidebar-whatsapp" className="mt-2 w-full border border-white/30 py-3 text-[11px] uppercase tracking-[0.22em] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-600 hover:border-emerald-600"><MessageCircle size={13}/> WhatsApp</a>
                            </div>
                            {b.is_sample && <p className="text-[11px] text-black/45">This is a sample brand listing for demonstration purposes.</p>}
                        </div>
                    </aside>
                </section>
            </div>
        </PageShell>
    );
}
